import { inView, animate } from 'https://cdn.jsdelivr.net/npm/motion@12.23.12/+esm';

/* ================================
   Utilities & global config
================================== */
const RMQ = '(prefers-reduced-motion: reduce)';
const prefersReduced = () => !!(window.matchMedia && window.matchMedia(RMQ).matches);
const hasIO = 'IntersectionObserver' in window;

const REVEAL_DEFAULT_DUR = 0.6;

const DRAW_SPEED_PX_PER_SEC = 900; // higher = faster
const DRAW_STAGGER = 0.02;         // lower = less gap between shapes
const DRAW_REVEAL_AMOUNT = 0.3;    // how much visible to start draw

const onReady = (fn) =>
  (document.readyState !== 'loading'
    ? fn()
    : document.addEventListener('DOMContentLoaded', fn));

const readNumber = (el, dataName, cssVar, fallback) => {
  if (!el) return fallback;
  const fromData = el.dataset ? el.dataset[dataName] : null;
  if (fromData != null && fromData !== '') {
    const n = Number(fromData);
    if (!Number.isNaN(n)) return n;
  }
  if (cssVar) {
    const css = parseFloat(getComputedStyle(el).getPropertyValue(cssVar));
    if (!Number.isNaN(css)) return css;
  }
  return fallback;
};

/* ================================
   1) Reveal on scroll (once-only)
   Classes: .mo_reveal-up / -left / -right / .mo_slide-up / .mo_reveal-on-scroll
   Optional per-element overrides:
     data-mo-distance="120"   (px; negative to move left/up)
     data-mo-duration="0.8"   (seconds)
     CSS vars: --mo-reveal-distance, --mo-reveal-duration
================================== */
const revealOnce = (selector, baseKeyframes, opts = { amount: 0.25 }) => {
  if (!hasIO) {
    // Legacy fallback: just show everything
    document.querySelectorAll(selector).forEach((el) => el.classList.add('revealed'));
    return;
  }

  inView(selector, (el) => {
    if (prefersReduced()) { el.classList.add('revealed'); return; }
    if (el.dataset.revealed === '1') return;
    el.dataset.revealed = '1';

    // Clone keyframes and allow per-element distance/duration
    const kf = { ...baseKeyframes };
    const dist = readNumber(el, 'moDistance', '--mo-reveal-distance', NaN);
    if (Number.isFinite(dist)) {
      if ('y' in kf) kf.y = [dist, 0];
      if ('x' in kf) kf.x = [dist, 0];
    }
    const dur = readNumber(el, 'moDuration', '--mo-reveal-duration', REVEAL_DEFAULT_DUR);

    const ctl = animate(el, kf, { duration: dur, easing: 'easeOut' });
    ctl.finished
      .then(() => el.classList.add('revealed'))
      .catch(() => el.classList.add('revealed')); // fail-safe
  }, opts);
};

// Presets (ensure CSS start states use matching axes with units, e.g., translateY(100px))
revealOnce('.mo_reveal-up',        { opacity: [0, 1], y: [100, 0] });
revealOnce('.mo_reveal-left',      { opacity: [0, 1], x: [-100, 0] });
revealOnce('.mo_reveal-right',     { opacity: [0, 1], x: [100, 0] });
revealOnce('.mo_slide-up',         { opacity: [0, 1], y: [100, 0] });
revealOnce('.mo_reveal-on-scroll', { opacity: [0, 1], y: [16, 0] });

/* ================================
   2) Stagger children (robust)
   Class: .mo_stagger -> animates items once
   Optional:
     data-mo-items=".selector"       // override which descendants to animate (default :scope > *)
     data-mo-distance="16"           // px vertical offset (default 12)
     data-mo-duration="0.8"          // seconds (default 1.0)
     data-mo-stagger="0.12"          // seconds between items (default 0.2)
     CSS vars: --mo-stagger-distance, --mo-stagger-duration, --mo-stagger-step
================================== */
const bindStagger = () => {
  if (!hasIO) {
    // Legacy: no IntersectionObserver, just show content
    document.querySelectorAll('.mo_stagger').forEach((section) => {
      const sel = section.dataset.moItems || ':scope > *';
      section.querySelectorAll(sel).forEach((el) => {
        el.style.opacity = 1; el.style.transform = 'none';
      });
    });
    return;
  }

  inView('.mo_stagger', (section) => {
    const sel  = section.dataset.moItems || ':scope > *';
    const kids = Array.from(section.querySelectorAll(sel));
    if (!kids.length) return;

    if (prefersReduced()) {
      kids.forEach((el) => { el.style.opacity = 1; el.style.transform = 'none'; el.classList.add('revealed'); });
      return;
    }

    const dur  = readNumber(section, 'moDuration', '--mo-stagger-duration', 1.0);
    const step = readNumber(section, 'moStagger',  '--mo-stagger-step',     0.2);
    const dist = readNumber(section, 'moDistance', '--mo-stagger-distance', 12);

    kids.forEach((child, i) => {
      // Safe start state (in case CSS starter didn’t apply)
      if (!child.style.opacity)   child.style.opacity = 0;
      if (!child.style.transform) child.style.transform = `translateY(${dist}px)`;

      const ctl = animate(child, { opacity: [0, 1], y: [dist, 0] }, { duration: dur, delay: i * step, easing: 'easeOut' });
      ctl.finished
        .then(() => child.classList.add('revealed'))
        .catch(() => child.classList.add('revealed'));
    });
  }, { amount: 0.2 });
};
bindStagger();

/* ================================
   3) Count-up numbers
   Class: .mo_count-up
   Optional:
     data-count-from="0"
     data-count-to="1234"
     data-count-duration="1.2"
     data-count-decimals="0"
================================== */
const bindCountUp = () => {
  const showImmediate = (el) => {
    const to  = readNumber(el, 'countTo', null, parseFloat(el.textContent?.trim() || '0') || 0);
    const dec = Math.max(0, readNumber(el, 'countDecimals', null, 0));
    el.textContent = to.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
  };

  if (!hasIO) {
    document.querySelectorAll('.mo_count-up').forEach(showImmediate);
    return;
  }

  inView('.mo_count-up', (el) => {
    const to   = readNumber(el, 'countTo',       null, parseFloat(el.textContent?.trim() || '0') || 0);
    const from = readNumber(el, 'countFrom',     null, 0);
    const dur  = readNumber(el, 'countDuration', null, 1.2);
    const dec  = Math.max(0, readNumber(el, 'countDecimals', null, 0));

    if (prefersReduced() || dur <= 0) { showImmediate(el); return; }

    const fmt = new Intl.NumberFormat(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
    const ctl = animate(from, to, {
      duration: dur,
      easing: 'easeOut',
      onUpdate: (v) => { el.textContent = fmt.format(v); }
    });
    ctl.finished.catch(() => { el.textContent = fmt.format(to); });
  }, { amount: 0.6 });
};
bindCountUp();

/* ================================
   4) SVG Draw-on-view
   Wrapper or SVG class: .mo_draw-on-view
   Per-scope overrides:
     data-draw-speed   (px/sec), data-draw-stagger (sec)
     --draw-speed, --draw-stagger (CSS vars)
================================== */
function primeSvg(svg, scope) {
  if (!svg || svg.dataset._prepped === '1') return;

  const shapes = svg.querySelectorAll('path, line, polyline, polygon, rect, circle, ellipse');
  shapes.forEach((el) => {
    // Lock original colors from attributes (so theme CSS can’t override)
    const fillAttr   = el.getAttribute('fill');
    const strokeAttr = el.getAttribute('stroke');
    const strokeW    = el.getAttribute('stroke-width');
    if (fillAttr   != null) el.style.fill        = fillAttr;
    if (strokeAttr != null) el.style.stroke      = strokeAttr;
    if (strokeW    != null) el.style.strokeWidth = strokeW;

    const cs = getComputedStyle(el);
    const hasStroke = cs.stroke && cs.stroke !== 'none' && (parseFloat(cs.strokeWidth) || 0) > 0;

    // Hide fills until drawing progresses so art appears from nothing
    if (fillAttr && fillAttr !== 'none') el.style.fillOpacity = '0';

    if (hasStroke) {
      let len = 300;
      try { if (typeof el.getTotalLength === 'function') len = el.getTotalLength(); } catch {}
      el.dataset._len = String(len);
      el.style.strokeDasharray  = String(len);
      el.style.strokeDashoffset = String(len);
      el.style.strokeOpacity    = '0';
      el.style.willChange       = 'stroke-dashoffset, stroke-opacity';
    }
  });

  scope.classList.add('mo_draw-prepped'); // make the (empty) art visible
  svg.dataset._prepped = '1';
}

function drawSvg(scope) {
  const svg = scope.tagName?.toLowerCase() === 'svg' ? scope : scope.querySelector('svg');
  if (!svg || scope.dataset._animated === '1') return;
  scope.dataset._animated = '1';

  const speed   = readNumber(scope, 'drawSpeed',   '--draw-speed',   DRAW_SPEED_PX_PER_SEC);
  const stagger = readNumber(scope, 'drawStagger', '--draw-stagger', DRAW_STAGGER);

  const shapes = svg.querySelectorAll('path, line, polyline, polygon, rect, circle, ellipse');
  shapes.forEach((el, i) => {
    if (el.dataset._done === '1') return;

    const len = parseFloat(el.dataset._len || '0');
    const hasStroke = len > 0;

    const tasks = [];

    if (hasStroke) {
      const duration = Math.max(0.35, len / speed);
      const ctlStroke = animate(el, { strokeDashoffset: [len, 0], strokeOpacity: [0, 1] }, {
        duration, easing: 'linear', delay: i * stagger
      });
      tasks.push(ctlStroke.finished.catch(() => {}));
    }

    const hasFill = (el.style.fill && el.style.fill !== 'none') || el.getAttribute('fill');
    if (hasFill) {
      const fillDelay = i * stagger + (hasStroke ? Math.min(0.6, Math.max(0.15, (len / speed) * 0.35)) : 0.15);
      const ctlFill = animate(el, { fillOpacity: [0, 1] }, { duration: 0.35, easing: 'easeOut', delay: fillDelay });
      tasks.push(ctlFill.finished.catch(() => {}));
    }

    Promise.all(tasks).then(() => {
      el.style.strokeDasharray  = '';
      el.style.strokeDashoffset = '';
      el.style.strokeOpacity    = '';
      el.style.fillOpacity      = '';
      el.style.willChange       = '';
      el.dataset._done = '1';
    });
  });
}

const bindDraw = () => {
  if (!hasIO) {
    // Legacy fallback: just show artwork immediately
    onReady(() => {
      document.querySelectorAll('.mo_draw-on-view').forEach((scope) => {
        const svg = scope.tagName?.toLowerCase() === 'svg' ? scope : scope.querySelector('svg');
        if (!svg) return;
        primeSvg(svg, scope);
        svg.querySelectorAll('*').forEach((el) => {
          el.style.strokeDasharray  = '';
          el.style.strokeDashoffset = '';
          el.style.strokeOpacity    = '';
          el.style.fillOpacity      = '';
          el.style.willChange       = '';
        });
        scope.classList.add('mo_draw-prepped');
        scope.dataset._animated = '1';
      });
    });
    return;
  }

  // Prep early (before on-screen) to avoid “visible→disappear→draw” flicker
  const prepIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      const scope = e.target;
      const svg = scope.tagName?.toLowerCase() === 'svg' ? scope : scope.querySelector('svg');
      if (svg) primeSvg(svg, scope);
      prepIO.unobserve(scope);
    });
  }, { root: null, rootMargin: '200% 0px 200% 0px', threshold: 0 });

  const observeAll = () => document.querySelectorAll('.mo_draw-on-view').forEach((el) => prepIO.observe(el));
  onReady(observeAll);
  new MutationObserver(observeAll).observe(document.body, { childList: true, subtree: true });

  // Draw on enter (once)
  inView('.mo_draw-on-view', (scope) => { drawSvg(scope); }, { amount: DRAW_REVEAL_AMOUNT });

  // Also prep above-the-fold immediately
  onReady(() => {
    document.querySelectorAll('.mo_draw-on-view').forEach((scope) => {
      const svg = scope.tagName?.toLowerCase() === 'svg' ? scope : scope.querySelector('svg');
      if (svg) primeSvg(svg, scope);
    });
  });

  // Clean up observers on tab hide
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') prepIO.disconnect();
  });
};
bindDraw();

/* ================================
   5) Tilt element (smoothed, no snap on exit)
   .mo_tilt — smooth spring tilt (no snap, no scale runaway)
    Add class "mo_tilt" to any element (img, card, div…).
   Markup:
    <img class="mo_tilt" src="..." /> 

   Optional data-attrs:
     data-tilt-max="12"           // degrees
     data-tilt-scale="1.03"       // hover lift
     data-tilt-perspective="900"  // px
     data-tilt-reverse="false"
     data-tilt-stiffness="380"    // spring k (higher = snappier)
     data-tilt-damping="30"       // spring damping (crit ~ 2*sqrt(k))
     data-tilt-edgepad="0.06"     // 0..0.2, trims shaky edges
============================================================ */
(() => {
  const SEL = '.mo_tilt';
  const prefersReduced = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const defaults = {
    max: 12,
    scale: 1.03,
    perspective: 900,
    reverse: false,
    stiffness: 380,
    damping: 30,
    edgepad: 0.06
  };

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const num   = (el,k,d) => (Number.isFinite(parseFloat(el.dataset?.[k])) ? parseFloat(el.dataset[k]) : d);
  const bool  = (el,k,d) => {
    const v = (el.dataset?.[k] ?? '').toString().trim().toLowerCase();
    return v ? ['1','true','yes','y','on'].includes(v) : d;
  };

  const states = new WeakMap();

  function bind(el){
    if (states.has(el) || prefersReduced()) return;

    const cs = getComputedStyle(el);
    const author = cs.transform && cs.transform !== 'none' ? cs.transform : '';
    // Keep a stable transform chain at all times (prevents snap)
    const base = author ? author + ' ' : '';

    const st = {
      rect: el.getBoundingClientRect(),
      max: num(el,'tiltMax',defaults.max),
      scale: num(el,'tiltScale',defaults.scale),
      perspective: num(el,'tiltPerspective',defaults.perspective),
      reverse: bool(el,'tiltReverse',defaults.reverse),
      k: num(el,'tiltStiffness',defaults.stiffness),
      c: num(el,'tiltDamping',defaults.damping),
      pad: clamp(num(el,'tiltEdgepad',defaults.edgepad), 0, 0.2),

      // spring state
      rx: 0, ry: 0, s: 1,
      vrx: 0, vry: 0, vs: 0,
      trx: 0, try: 0, ts: 1,

      tracking: false,
      raf: 0,
      lastT: 0,

      compose(rx, ry, s){
        // authorTransform FIRST, then tilt chain; order is stable 100% of the time.
        return `${base}perspective(${st.perspective}px) rotateX(${rx}deg) rotateY(${ry}deg) scale(${s})`;
      }
    };

    states.set(el, st);

    function easeInOutSine(x){ return Math.sin((x * Math.PI) / 2); } // smooth response near edges

    function setTargetsFromEvent(e){
      // Re-measure for scrolling layouts (helps with edge jitter when page moves)
      st.rect = el.getBoundingClientRect();
      const r = st.rect;
      const w = Math.max(1, r.width), h = Math.max(1, r.height);

      // Normalize 0..1 with a little padding to avoid jitter when the cursor hits the border
      const pad = st.pad;
      const px = clamp((e.clientX - r.left) / w, pad, 1 - pad);
      const py = clamp((e.clientY - r.top) / h, pad, 1 - pad);

      // Map to -1..1 and ease to smooth the edges
      const nx = easeInOutSine((px - 0.5) * 2);
      const ny = easeInOutSine((py - 0.5) * 2);

      const dir = st.reverse ? -1 : 1;
      const rx = dir * (-ny) * st.max; // tilt X by vertical offset
      const ry = dir * ( nx) * st.max; // tilt Y by horizontal offset

      st.trx = clamp(rx, -st.max, st.max);
      st.try = clamp(ry, -st.max, st.max);
      st.ts  = st.scale; // lift while hovering
    }

    // Critically-damped spring step (semi-implicit Euler)
    function springStep(x, v, target, dt, k, c){
      // F = k(x_t - x) - c*v
      const a = k * (target - x) - c * v;
      const nv = v + a * dt;
      const nx = x + nv * dt;
      return [nx, nv];
    }

    function tick(t){
      st.raf = requestAnimationFrame(tick);

      // Time step in seconds, clamp to avoid jumps on tab switches
      const dt = Math.min(0.034, Math.max(0.001, (st.lastT ? (t - st.lastT) : 16) / 1000));
      st.lastT = t;

      // Integrate springs
      [st.rx, st.vrx] = springStep(st.rx, st.vrx, st.trx, dt, st.k, st.c);
      [st.ry, st.vry] = springStep(st.ry, st.vry, st.try, dt, st.k, st.c);
      [st.s , st.vs ] = springStep(st.s , st.vs , st.ts , dt, st.k, st.c*1.1);

      el.style.transform = st.compose(st.rx, st.ry, st.s);

      // Stop when everything is settled (saves CPU)
      if (!st.tracking){
        const near =
          Math.abs(st.rx - st.trx) < 0.02 &&
          Math.abs(st.ry - st.try) < 0.02 &&
          Math.abs(st.s  - st.ts ) < 0.001 &&
          Math.abs(st.vrx) < 0.02 &&
          Math.abs(st.vry) < 0.02 &&
          Math.abs(st.vs ) < 0.01;

        if (near){
          cancelAnimationFrame(st.raf);
          st.raf = 0;
          st.lastT = 0;
        }
      }
    }

    function ensureRAF(){
      if (!st.raf) st.raf = requestAnimationFrame(tick);
    }

    function onEnter(e){
      if ((e.pointerType || 'mouse') === 'touch') return; // ignore touch
      st.tracking = true;
      setTargetsFromEvent(e);
      ensureRAF();
    }

    function onMove(e){
      if (!st.tracking) return;
      setTargetsFromEvent(e);
      ensureRAF();
    }

    function onLeave(){
      st.tracking = false;
      st.trx = 0; st.try = 0; st.ts = 1;   // spring back smoothly, no chain swap
      ensureRAF();
    }

    function onCancel(){ onLeave(); }
    function onResize(){ st.rect = el.getBoundingClientRect(); }

    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    el.addEventListener('pointercancel', onCancel);
    window.addEventListener('resize', onResize);

    // Public unbind if you ever need it
    el.__moTiltDestroy__ = () => {
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('resize', onResize);
      if (st.raf) cancelAnimationFrame(st.raf);
      states.delete(el);
      // leave it in the “rest” transform (author + identity tilt)
      el.style.transform = `${base}perspective(${st.perspective}px) rotateX(0deg) rotateY(0deg) scale(1)`;
    };
  }

  function bindAll(root=document){ root.querySelectorAll(SEL).forEach(bind); }

  if (document.readyState !== 'loading') bindAll();
  else document.addEventListener('DOMContentLoaded', bindAll);

  // Auto-bind future nodes
  new MutationObserver(muts => {
    muts.forEach(m => m.addedNodes.forEach(n => {
      if (n.nodeType !== 1) return;
      if (n.matches?.(SEL)) bind(n);
      n.querySelectorAll?.(SEL).forEach(bind);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* ================================
   (Optional) Light MO for late-added nodes
   (Your inView observers are already global; this is just parity with prior code)
================================== */
const lightMO = new MutationObserver(() => {});
lightMO.observe(document.body, { childList: true, subtree: true });
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') lightMO.disconnect();
});

/* ================================
   6) Scroll-scrubbed SVG draw (.mo_draw-on-scroll)
   - Strokes draw/undraw with scroll
   - Fills fade in near the end
   - Fill-outline proxies: default = PATHS ONLY (no rect/circle/ellipse)
   Markup:
    <svg class="mo_draw-on-scroll">
      ...
    </svg>
================================== */
(() => {
  const prefersReduced = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const SEL = '.mo_draw-on-scroll';

  const DEFAULTS = {
    stagger: 0.08,         // each shape starts this much later (fraction of total progress)
    windowStart: 0.80,     // start drawing when element’s top hits 80% of viewport
    windowEnd: 0.80,       // finish when element’s bottom hits 80% of viewport
    fillThreshold: 0.80,   // when fills begin to fade in (0–1)
    // Outline mode for fill-only shapes:
    //   "paths" = only <path> (default; avoids rect grids)
    //   "all"   = any shape (path, line, polyline, polygon, rect, circle, ellipse)
    //   "off"   = disable proxies entirely
    outlineMode: 'paths',
  };

  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const easeOut = t => 1 - Math.pow(1 - t, 3);

  // parse dataset booleans / enums safely
  const getEnum = (el, name, def) => {
    const v = el.dataset?.[name];
    return (v == null || v === '') ? def : String(v).trim().toLowerCase();
  };
  const getNum = (el, name, def) => {
    const v = parseFloat(el.dataset?.[name]);
    return Number.isFinite(v) ? v : def;
  };

  // Try to compute a usable length for any geometry
  function shapeLength(el) {
    try { if (typeof el.getTotalLength === 'function') return el.getTotalLength(); } catch {}
    const b = el.getBBox?.(); if (b) return 2 * (b.width + b.height);
    return 0;
  }

  // Make a stroke-only proxy for a fill-only PATH
  function makePathProxy(pathEl) {
    const d = pathEl.getAttribute('d');
    if (!d) return null;
    const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', d);
    p.setAttribute('fill', 'none');
    // Use computed fill color as stroke so it matches the art, not pure black
    const cs = getComputedStyle(pathEl);
    const strokeColor = cs.fill && cs.fill !== 'none' ? cs.fill : (cs.stroke || 'currentColor');
    p.setAttribute('stroke', strokeColor);
    // If original has a strokeWidth, reuse; else pick a thin line
    const sw = parseFloat(cs.strokeWidth);
    p.setAttribute('stroke-width', Number.isFinite(sw) && sw > 0 ? String(sw) : '1');
    p.setAttribute('vector-effect', 'non-scaling-stroke');
    p.setAttribute('data-mo-proxy', '1'); // mark for cleanup
    p.style.pointerEvents = 'none';
    return p;
  }

  function allowedForProxy(tag, outlineMode) {
    if (outlineMode === 'off') return false;
    if (outlineMode === 'all') return true;
    // default: 'paths'
    return tag === 'path';
  }

  function primeScope(scope) {
    // Cleanup/guard: if re-priming, remove old proxies
    const existing = scope.__mo_scrollDraw__;
    if (existing?.proxies?.length) {
      existing.proxies.forEach(n => n.remove());
    }

    const svg = scope.tagName?.toLowerCase() === 'svg' ? scope : scope.querySelector('svg');
    if (!svg) return;

    const shapes = [...svg.querySelectorAll('path, line, polyline, polygon, rect, circle, ellipse')];
    const items = [];
    const proxies = [];

    // Config per scope (with safe parsing)
    const userStagger = getNum(scope, 'drawStagger', DEFAULTS.stagger);
    const windowStart = clamp(getNum(scope, 'windowStart', DEFAULTS.windowStart));
    const windowEnd   = clamp(getNum(scope, 'windowEnd',   DEFAULTS.windowEnd));
    const fillThreshold = clamp(getNum(scope, 'fillThreshold', DEFAULTS.fillThreshold));
    const outlineMode = getEnum(scope, 'outlineFills', DEFAULTS.outlineMode); // 'paths' | 'all' | 'off'

    // First pass: build list of drawable stroke items; optionally add proxies for fill-only PATHs
    shapes.forEach((el) => {
      const tag = el.tagName.toLowerCase();
      const cs = getComputedStyle(el);

      const strokeWidth = parseFloat(cs.strokeWidth) || 0;
      const hasStroke = cs.stroke && cs.stroke !== 'none' && strokeWidth > 0;
      const hasFillAttr = el.getAttribute('fill');
      const hasFill = (cs.fill && cs.fill !== 'none') || (hasFillAttr && hasFillAttr !== 'none');

      // 1) Stroke shapes: set up dash drawing
      if (hasStroke) {
        const len = shapeLength(el) || 0;
        if (len > 0) {
          el.style.strokeDasharray = String(len);
          el.style.strokeDashoffset = String(len);
          el.style.willChange = 'stroke-dashoffset';
          items.push({ el, len, hasFill });
        }
      } else if (hasFill && allowedForProxy(tag, outlineMode)) {
        // 2) Fill-only PATH (or allowed) ➜ create a stroke proxy so it “draws”
        const proxy = tag === 'path' ? makePathProxy(el) : null;
        if (proxy) {
          // Put the proxy *before* the original so the real fill paints over it later
          el.parentNode?.insertBefore(proxy, el);
          const len = shapeLength(proxy) || 0;
          if (len > 0) {
            proxy.style.strokeDasharray = String(len);
            proxy.style.strokeDashoffset = String(len);
            proxy.style.willChange = 'stroke-dashoffset';
            items.push({ el: proxy, len, hasFill: false }); // proxy has no fill
            proxies.push(proxy);
          } else {
            proxy.remove();
          }
        }
      }

      // Fade real fills in near the end of the overall progress
      if (hasFill) {
        el.style.fillOpacity = '0';
        el.style.willChange = (el.style.willChange ? el.style.willChange + ', ' : '') + 'fill-opacity';
      }
    });

    // Auto-compress stagger so the last item starts by ~90% progress
    const n = items.length;
    const maxStartBy = 0.90; // last shape will begin by this overall progress
    const safeStagger = (n > 1) ? Math.min(userStagger, maxStartBy / (n - 1)) : 0;

    scope.__mo_scrollDraw__ = {
      svg, items, proxies,
      stagger: safeStagger,
      windowStart, windowEnd, fillThreshold,
    };
  }

  // Map element rect to progress 0..1 given "start" and "end" window anchors
  function progressForRect(rect, vh, start, end) {
    // Start when top crosses start*vh, finish when bottom crosses end*vh
    const startPx = vh * start;
    const endPx = vh * end;
    const total = (rect.height + startPx - endPx) || 1;
    const p = (startPx - rect.top) / total;
    return clamp(p);
  }

  function applyProgress(scope, t) {
    const data = scope.__mo_scrollDraw__;
    if (!data) return;

    const { items, stagger, fillThreshold } = data;
    if (!items.length) return;

    // Per-shape staggered progress
    const n = items.length;
    const span = Math.max(0.0001, 1 - stagger * (n - 1));

    items.forEach((it, i) => {
      const localStart = i * stagger;
      const localT = clamp((t - localStart) / span);

      // Stroke/proxy draw
      const off = (1 - localT) * it.len;
      it.el.style.strokeDashoffset = String(off);
    });

    // Fill fade (based on overall t for nicer reveal)
    // We fade the *real* elements that had a fill, not the proxies
    const svg = data.svg;
    svg.querySelectorAll(':scope path, :scope line, :scope polyline, :scope polygon, :scope rect, :scope circle, :scope ellipse').forEach(el => {
      const cs = getComputedStyle(el);
      const hasFillAttr = el.getAttribute('fill');
      const hasFill = (cs.fill && cs.fill !== 'none') || (hasFillAttr && hasFillAttr !== 'none');
      if (hasFill) {
        const ft = clamp((t - fillThreshold) / (1 - fillThreshold));
        el.style.fillOpacity = String(easeOut(ft));
      }
    });
  }

  // Track active scopes near the viewport
  const tracked = new Set();
  let ticking = false;

  function updateAll() {
    ticking = false;
    const vh = window.innerHeight || 0;
    tracked.forEach((scope) => {
      const data = scope.__mo_scrollDraw__;
      if (!data) return;

      const rect = scope.getBoundingClientRect?.();
      if (!rect) return;

      const t = prefersReduced() ? 1 : progressForRect(rect, vh, data.windowStart, data.windowEnd);
      applyProgress(scope, t);
    });
  }

  function onScrollOrResize() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateAll);
    }
  }

  // Observe elements entering/leaving with generous margins
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const el = e.target;
      if (e.isIntersecting) {
        primeScope(el);
        tracked.add(el);
        onScrollOrResize(); // immediate sync
      } else {
        tracked.delete(el);
      }
    });
  }, { root: null, rootMargin: '200% 0px 200% 0px' });

  function bindAll() {
    document.querySelectorAll(SEL).forEach((el) => {
      primeScope(el);
      io.observe(el);
    });
    onScrollOrResize();
  }

  // Initial bind + live DOM
  if (document.readyState !== 'loading') bindAll();
  else document.addEventListener('DOMContentLoaded', bindAll);

  new MutationObserver((muts) => {
    muts.forEach(m => {
      m.addedNodes.forEach((n) => {
        if (n.nodeType !== 1) return;
        if (n.matches?.(SEL)) { primeScope(n); io.observe(n); }
        n.querySelectorAll?.(SEL).forEach((el) => { primeScope(el); io.observe(el); });
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

  // Keep in sync while user resizes/scrolls (no animation timer = “pauses” when you stop)
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);
})();

/* ============================================================
  7) Follow Cursor (.mo_follow-mouse — springy follow-the-cursor on hover)
   Add class "mo_follow-mouse" to any element (img, div, etc.)

   Defaults (override via data-attrs):
     data-follow-max="48"         // max px translation from origin
     data-follow-axis="both"      // "both" | "x" | "y"
     data-follow-stiffness="120"  // spring k (higher = snappier)
     data-follow-damping="22"     // spring damping (crit ~ 2*sqrt(k))
     data-follow-global="false"   // if "true", follows cursor anywhere
                                   // (not just when hovered)
============================================================ */
(() => {
  const SEL = '.mo_follow-mouse';
  const prefersReduced = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const defaults = {
    max: 48,
    axis: 'both',
    k: 120,
    c: 22,
    global: false
  };

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const num   = (el,k,d) => (Number.isFinite(parseFloat(el.dataset?.[k])) ? parseFloat(el.dataset[k]) : d);
  const str   = (el,k,d) => (el.dataset?.[k] ?? d).toString();
  const bool  = (el,k,d) => {
    const v = (el.dataset?.[k] ?? '').toString().trim().toLowerCase();
    return v ? ['1','true','yes','on','y'].includes(v) : d;
  };

  // Global pointer for "global follow" mode
  const mouse = { x: 0, y: 0, inside: false };
  function bindGlobalPointer(){
    if (bindGlobalPointer.bound) return;
    bindGlobalPointer.bound = true;
    addEventListener('pointermove', e => {
      if (e.pointerType === 'touch') return;
      mouse.x = e.clientX; mouse.y = e.clientY; mouse.inside = true;
    }, { passive: true });
    addEventListener('pointerleave', () => { mouse.inside = false; });
  }

  const states = new WeakMap();

  // Simple spring (semi-implicit Euler)
  function springStep(x, v, target, dt, k, c){
    const a = k * (target - x) - c * v;
    const nv = v + a * dt;
    const nx = x + nv * dt;
    return [nx, nv];
  }

  function bind(el){
    if (states.has(el) || prefersReduced()) return;

    const cs = getComputedStyle(el);
    const author = (cs.transform && cs.transform !== 'none') ? cs.transform : '';
    const base = author ? author + ' ' : '';

    const st = {
      axis: str(el, 'followAxis', defaults.axis),                  // 'both' | 'x' | 'y'
      max:  Math.max(0, num(el, 'followMax', defaults.max)),       // px
      k:    Math.max(1, num(el, 'followStiffness', defaults.k)),
      c:    Math.max(1, num(el, 'followDamping', defaults.c)),
      global: bool(el, 'followGlobal', defaults.global),

      rect: el.getBoundingClientRect(),
      // current
      x: 0, y: 0, vx: 0, vy: 0,
      // targets
      tx: 0, ty: 0,

      tracking: false,
      raf: 0,
      lastT: 0,

      compose(x, y){
        // Stable transform chain: author + translate3d
        return `${base}translate3d(${x.toFixed(3)}px, ${y.toFixed(3)}px, 0)`;
      }
    };
    states.set(el, st);

    function measure(){ st.rect = el.getBoundingClientRect(); }

    function setTargetFromEvent(e){
      st.tracking = true;
      if (st.global){
        // global mode: aim at the pointer anywhere on screen
        st.tx = mouse.x - (st.rect.left + st.rect.width/2);
        st.ty = mouse.y - (st.rect.top + st.rect.height/2);
      } else {
        // hover mode: aim based on pointer inside this element
        const cx = st.rect.left + st.rect.width/2;
        const cy = st.rect.top  + st.rect.height/2;
        st.tx = e.clientX - cx;
        st.ty = e.clientY - cy;
      }

      // Axis lock
      if (st.axis === 'x') st.ty = 0;
      if (st.axis === 'y') st.tx = 0;

      // Clamp to max radius (unless max===0 which means “no movement”)
      const max = st.max;
      if (max > 0){
        const mag = Math.hypot(st.tx, st.ty);
        if (mag > max){
          const s = max / mag;
          st.tx *= s; st.ty *= s;
        }
      }
    }

    function release(){
      st.tracking = false;
      st.tx = 0; st.ty = 0;        // spring back smoothly
      ensureRAF();
    }

    function tick(t){
      st.raf = requestAnimationFrame(tick);

      const dt = Math.min(0.034, Math.max(0.001, (st.lastT ? (t - st.lastT) : 16) / 1000));
      st.lastT = t;

      // Re-measure occasionally when we’re active (for scrolling layouts)
      if (st.tracking && (t % 250 < 16)) measure();

      [st.x, st.vx] = springStep(st.x, st.vx, st.tx, dt, st.k, st.c);
      [st.y, st.vy] = springStep(st.y, st.vy, st.ty, dt, st.k, st.c);

      el.style.transform = st.compose(st.x, st.y);

      if (!st.tracking){
        const settled =
          Math.abs(st.x - st.tx) < 0.02 &&
          Math.abs(st.y - st.ty) < 0.02 &&
          Math.abs(st.vx) < 0.02 &&
          Math.abs(st.vy) < 0.02;

        if (settled){
          cancelAnimationFrame(st.raf);
          st.raf = 0;
          st.lastT = 0;
        }
      }
    }

    function ensureRAF(){ if (!st.raf) st.raf = requestAnimationFrame(tick); }

    // Events
    function onEnter(e){
      if (e.pointerType === 'touch') return;
      measure();
      setTargetFromEvent(e);
      ensureRAF();
    }
    function onMove(e){
      if (e.pointerType === 'touch') return;
      setTargetFromEvent(e);
      ensureRAF();
    }
    function onLeave(){ release(); }
    function onCancel(){ release(); }
    function onResize(){ measure(); }

    if (st.global){
      bindGlobalPointer();
      // Start an idle loop that wakes when mouse is inside viewport
      const watch = () => {
        if (mouse.inside){
          // pretend a move event with global coords
          setTargetFromEvent({ clientX: mouse.x, clientY: mouse.y });
          ensureRAF();
        } else if (!st.tracking && !st.raf && (st.x || st.y)) {
          // ensure we return to origin if mouse left the window
          release();
        }
        requestAnimationFrame(watch);
      };
      watch();
      // Also allow hover to “wake” quickly
      el.addEventListener('pointerenter', onEnter);
      el.addEventListener('pointerleave', onLeave);
      el.addEventListener('pointercancel', onCancel);
    } else {
      // Classic hover-follow
      el.addEventListener('pointerenter', onEnter);
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
      el.addEventListener('pointercancel', onCancel);
    }

    addEventListener('resize', onResize);

    // Optional: public destroy
    el.__moFollowDestroy__ = () => {
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('pointercancel', onCancel);
      removeEventListener('resize', onResize);
      if (st.raf) cancelAnimationFrame(st.raf);
      states.delete(el);
      el.style.transform = `${base}translate3d(0px, 0px, 0)`;
    };
  }

  function bindAll(root = document){
    root.querySelectorAll(SEL).forEach(bind);
  }

  if (document.readyState !== 'loading') bindAll();
  else document.addEventListener('DOMContentLoaded', bindAll);

  // Auto-bind future nodes
  new MutationObserver(muts => {
    muts.forEach(m => m.addedNodes.forEach(n => {
      if (n.nodeType !== 1) return;
      if (n.matches?.(SEL)) bind(n);
      n.querySelectorAll?.(SEL).forEach(bind);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* ============================================================
   8) .mo_text-wave — split text into characters and animate them
   Usage: <h1 class="mo_text-wave">Wavy headline</h1>

   Options (data-* on the element):
     data-wave-amp="8px"     // vertical amplitude; px | em | rem (default 8px)
     data-wave-speed="1.5"   // cycles per second (default 1.5)
     data-wave-gap="0.35"    // radians phase step per char (default 0.35)
     data-wave-rot="6"       // rotate deg at crests (default 6)
============================================================ */
(() => {
  const SEL = '.mo_text-wave';
  const DEF = { amp: '8px', speed: 0.3, gap: 0.35, rot: 6 };
  const TAU = Math.PI * 2;

  const prefersReduced = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const toPx = (el, v, fallbackPx) => {
    if (v == null) return fallbackPx;
    const s = ('' + v).trim();
    const n = parseFloat(s);
    if (!isFinite(n)) return fallbackPx;
    const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const fs   = parseFloat(getComputedStyle(el).fontSize) || 16;
    if (s.endsWith('rem')) return n * root;
    if (s.endsWith('em'))  return n * fs;
    return n; // assume px
  };

  // Split ONLY text nodes; preserve whitespace nodes; leave nested elements intact
  function splitTextIn(el){
    const spans = [];
    const tw = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    const jobs = [];
    let node;
    while ((node = tw.nextNode())) {
      // Skip empty nodes
      if (!node.nodeValue) continue;
      jobs.push(node);
    }
    jobs.forEach(textNode => {
      const txt = textNode.nodeValue;
      const frag = document.createDocumentFragment();
      // Keep whitespace chunks as plain text, letters as spans
      txt.split(/(\s+)/).forEach(chunk => {
        if (!chunk) return;
        if (/^\s+$/.test(chunk)) { // whitespace
          frag.appendChild(document.createTextNode(chunk));
        } else {
          for (const ch of chunk){
            const span = document.createElement('span');
            span.className = 'mo_wave-ch';
            span.textContent = ch;
            // Small perf hint per char
            span.style.transform = 'translate3d(0,0,0)';
            frag.appendChild(span);
            spans.push(span);
          }
        }
      });
      textNode.parentNode.replaceChild(frag, textNode);
    });
    return spans;
  }

  const states = new WeakMap();

  function bind(el){
    if (states.has(el)) return;
    if (prefersReduced()) return; // respect user motion settings

    // read options
    const amp   = toPx(el, el.dataset.waveAmp ?? DEF.amp, 8);
    const speed = parseFloat(el.dataset.waveSpeed ?? DEF.speed) || DEF.speed;
    const gap   = parseFloat(el.dataset.waveGap   ?? DEF.gap)   || DEF.gap;
    const rot   = parseFloat(el.dataset.waveRot   ?? DEF.rot)   || DEF.rot;

    // Split once
    const spans = splitTextIn(el);
    if (!spans.length) return;

    const st = {
      spans,
      ampPx: amp,
      speed, gap, rot,
      seed: Math.random() * TAU, // de-synchronize start phase
      visible: false,
      ramp: 0, targetRamp: 0,
      raf: 0, lastT: 0,
    };
    states.set(el, st);

    // Recompute amp on resize if author used em/rem
    function onResize(){
      st.ampPx = toPx(el, el.dataset.waveAmp ?? DEF.amp, 8);
    }
    addEventListener('resize', onResize);

    function tick(t){
      st.raf = requestAnimationFrame(tick);
      const now = t / 1000;

      // Smoothly approach targetRamp (0..1) so it never snaps
      const dt = Math.min(0.05, Math.max(0.001, st.lastT ? (now - st.lastT) : 0.016));
      st.lastT = now;
      // critically damped ramp toward target
      const k = 12; // responsiveness of ramp
      st.ramp += (st.targetRamp - st.ramp) * (1 - Math.exp(-k * dt));

      const phaseT = TAU * st.speed * now + st.seed;
      const A = st.ampPx * st.ramp;
      const R = st.rot   * st.ramp;

      // Write transforms
      for (let i = 0; i < st.spans.length; i++){
        const φ = phaseT + i * st.gap;
        const y = Math.sin(φ) * A;
        const r = Math.cos(φ) * R;
        st.spans[i].style.transform = `translate3d(0, ${y.toFixed(2)}px, 0) rotate(${r.toFixed(2)}deg)`;
      }

      // Stop loop when fully settled and not visible
      if (!st.visible && st.ramp < 0.001){
        cancelAnimationFrame(st.raf);
        st.raf = 0;
        st.lastT = 0;
      }
    }

    function ensureRAF(){ if (!st.raf) st.raf = requestAnimationFrame(tick); }

    // Only animate when in (or near) viewport
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.target !== el) return;
        st.visible = e.isIntersecting;
        st.targetRamp = e.isIntersecting ? 1 : 0;
        if (e.isIntersecting) ensureRAF(); else ensureRAF(); // keep running to ramp out
      });
    }, { root: null, rootMargin: '150px 0px' });
    io.observe(el);

    // Store a destroy hook if you ever need it
    el.__mo_waveDestroy__ = () => {
      io.disconnect();
      removeEventListener('resize', onResize);
      if (st.raf) cancelAnimationFrame(st.raf);
      // (Not restoring original text here; call only if you keep the split)
      states.delete(el);
    };

    // Kick once in case it's already visible
    ensureRAF();
  }

  function bindAll(root = document){
    root.querySelectorAll(SEL).forEach(bind);
  }

  if (document.readyState !== 'loading') bindAll();
  else document.addEventListener('DOMContentLoaded', bindAll);

  // Live-bind future nodes
  new MutationObserver(muts => {
    muts.forEach(m => m.addedNodes.forEach(n => {
      if (n.nodeType !== 1) return;
      if (n.matches?.(SEL)) bind(n);
      n.querySelectorAll?.(SEL).forEach(bind);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();